// Translations for Kompass Heidelberg — AR / DE / EN / FA
// Content adapted from the brief and migrantis.de service descriptions.

export const LANGUAGES = [
  { code: "ar", label: "العربية", native: "العربية", dir: "rtl", flag: "🇸🇦" },
  { code: "de", label: "Deutsch", native: "Deutsch", dir: "ltr", flag: "🇩🇪" },
  { code: "en", label: "English", native: "English", dir: "ltr", flag: "🇬🇧" },
  { code: "fa", label: "فارسی", native: "فارسی", dir: "rtl", flag: "🇮🇷" },
];

export const translations = {
  en: {
    nav: {
      about: "About",
      services: "Services",
      values: "Why Kompass",
      contact: "Contact",
      cta: "Book a consultation",
    },
    hero: {
      eyebrow: "Heidelberg · Service for Migrants",
      title_1: "Your compass through",
      title_accent: "life in Germany",
      subtitle:
        "Kompass is a translation and consulting office for migrants in Heidelberg. We guide you through immigration, asylum, paperwork, official appointments, and the search for an Ausbildung — calmly, professionally, and in your language.",
      cta_primary: "Book a free consultation",
      cta_secondary: "See our services",
      stat_1: "Languages",
      stat_2: "Years of experience",
      stat_3: "Families supported",
    },
    about: {
      eyebrow: "About Kompass",
      title: "A trusted partner for your new life in Germany",
      lead:
        "We know how overwhelming the German bureaucracy can feel — letters in formal language, confusing forms, long waits at the Ausländerbehörde. Kompass stands by your side, translates, explains, and walks with you, step by step.",
      pillars: [
        {
          title: "Professional service",
          text: "Expert advice for a successful integration in Germany — clear, precise and reliable.",
        },
        {
          title: "Reliable advice",
          text: "First-class guidance and tailored solutions for your individual situation.",
        },
        {
          title: "Experienced team",
          text: "Years of experience working with migrants and refugees — we understand both worlds.",
        },
      ],
    },
    services: {
      eyebrow: "Our services",
      title: "Everything you need, in one place",
      subtitle:
        "From your first appointment at the foreigners' office to a signed apprenticeship contract — we accompany you the whole way.",
      list: [
        {
          title: "Investor support",
          text: "Comprehensive support for investors and founders who want to build a company in Germany — from the idea to legal advice and implementation.",
        },
        {
          title: "Immigration & asylum advice",
          text: "Personal counselling for immigration, residence and asylum cases. We translate decisions, prepare documents and explain your options.",
        },
        {
          title: "Accompaniment to appointments",
          text: "We accompany you to authorities, doctors, schools and the Ausländerbehörde — and translate live so you understand every word.",
        },
        {
          title: "Registrations & paperwork",
          text: "Anmeldung, Krankenkasse, Kindergeld, Elterngeld, tax ID — we fill out the forms with you and submit them correctly.",
        },
        {
          title: "Ausbildung search",
          text: "We support young migrants in finding an apprenticeship and accompany them through the entire application process.",
        },
        {
          title: "Skilled-worker immigration",
          text: "Recognition of foreign qualifications, job hunting, visa preparation — your path to working in Germany.",
        },
        {
          title: "Translation services",
          text: "Reliable Arabic ↔ German ↔ English translations of certificates, letters, contracts and official documents.",
        },
        {
          title: "Integration support",
          text: "Help with finding accommodation, dealing with authorities and navigating daily life — so integration feels natural.",
        },
      ],
    },
    values: {
      eyebrow: "Why Kompass",
      title: "We don't just translate words — we translate situations",
      points: [
        { k: "Multilingual", v: "Arabic, German, English and Persian — under one roof." },
        { k: "Confidential", v: "Your story stays with us. Always discreet, always respectful." },
        { k: "Personal", v: "No call-centers. You speak with the same person every time." },
        { k: "Local", v: "Right in the heart of Heidelberg — easy to reach by tram and train." },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk about your situation",
      lead:
        "Write to us in any of our four languages. We usually reply within one working day.",
      form: {
        name: "Your name",
        email: "Email address",
        subject: "Subject",
        message: "How can we help you?",
        submit: "Send message",
        sending: "Sending…",
        success: "Thank you! We received your message and will get back to you shortly.",
        error: "Something went wrong. Please try again or call us directly.",
      },
      info: {
        address_label: "Office",
        address: "Eppelheimer Str. 21, 69115 Heidelberg",
        phone_label: "Phone",
        phone: "+49 178 164 78 73",
        email_label: "Email",
        email: "info@kompass-heidelberg.de",
        web_label: "Website",
        web: "www.kompass-heidelberg.de",
        hours_label: "Opening hours",
        hours: "By appointment",
      },
    },
    footer: {
      tagline: "Service for Migrants",
      rights: "All rights reserved.",
      built: "Heidelberg, Germany",
    },
  },

  de: {
    nav: {
      about: "Über uns",
      services: "Leistungen",
      values: "Warum Kompass",
      contact: "Kontakt",
      cta: "Termin vereinbaren",
    },
    hero: {
      eyebrow: "Heidelberg · Service für Migranten",
      title_1: "Ihr Kompass durch das",
      title_accent: "Leben in Deutschland",
      subtitle:
        "Kompass ist ein Übersetzungs- und Beratungsbüro für Migranten in Heidelberg. Wir begleiten Sie bei Migration, Asyl, Behördengängen, Anmeldungen und der Suche nach einer Ausbildung — ruhig, professionell und in Ihrer Sprache.",
      cta_primary: "Kostenlose Beratung buchen",
      cta_secondary: "Unsere Leistungen",
      stat_1: "Sprachen",
      stat_2: "Jahre Erfahrung",
      stat_3: "Begleitete Familien",
    },
    about: {
      eyebrow: "Über Kompass",
      title: "Ein verlässlicher Partner für Ihr neues Leben in Deutschland",
      lead:
        "Wir wissen, wie überwältigend die deutsche Bürokratie wirken kann — Briefe im Amtsdeutsch, verwirrende Formulare, lange Wartezeiten in der Ausländerbehörde. Kompass steht an Ihrer Seite, übersetzt, erklärt und geht mit Ihnen Schritt für Schritt.",
      pillars: [
        {
          title: "Professioneller Service",
          text: "Fachkundige Beratung für eine erfolgreiche Integration in Deutschland — klar, präzise und verlässlich.",
        },
        {
          title: "Zuverlässige Beratung",
          text: "Erstklassige Begleitung und maßgeschneiderte Lösungen für Ihre individuelle Situation.",
        },
        {
          title: "Erfahrenes Team",
          text: "Jahrelange Erfahrung in der Arbeit mit Migranten und Geflüchteten — wir kennen beide Welten.",
        },
      ],
    },
    services: {
      eyebrow: "Unsere Leistungen",
      title: "Alles was Sie brauchen, an einem Ort",
      subtitle:
        "Vom ersten Termin bei der Ausländerbehörde bis zum unterschriebenen Ausbildungsvertrag — wir begleiten Sie auf dem ganzen Weg.",
      list: [
        {
          title: "Unterstützung für Investoren",
          text: "Umfassende Unterstützung für Investoren und Gründer, die ein Unternehmen in Deutschland aufbauen möchten — von der Idee über die Rechtsberatung bis zur Umsetzung.",
        },
        {
          title: "Migration & Asylberatung",
          text: "Persönliche Beratung in Migrations-, Aufenthalts- und Asylfragen. Wir übersetzen Bescheide, bereiten Unterlagen vor und erklären Ihre Optionen.",
        },
        {
          title: "Begleitung zu Behörden",
          text: "Wir begleiten Sie zu Ämtern, Ärzten, Schulen und zur Ausländerbehörde — und übersetzen live, damit Sie jedes Wort verstehen.",
        },
        {
          title: "Anmeldungen & Anträge",
          text: "Anmeldung, Krankenkasse, Kindergeld, Elterngeld, Steuer-ID — wir füllen die Formulare gemeinsam mit Ihnen aus und reichen sie korrekt ein.",
        },
        {
          title: "Ausbildungssuche",
          text: "Wir unterstützen junge Migranten bei der Suche nach einer Ausbildung und begleiten sie durch den gesamten Bewerbungsprozess.",
        },
        {
          title: "Fachkräfteeinwanderung",
          text: "Anerkennung ausländischer Qualifikationen, Jobsuche, Visumsvorbereitung — Ihr Weg zum Arbeiten in Deutschland.",
        },
        {
          title: "Übersetzungen",
          text: "Zuverlässige Übersetzungen Arabisch ↔ Deutsch ↔ Englisch von Urkunden, Briefen, Verträgen und amtlichen Dokumenten.",
        },
        {
          title: "Integrationshilfe",
          text: "Hilfe bei der Wohnungssuche, beim Umgang mit Behörden und im Alltag — damit Integration sich natürlich anfühlt.",
        },
      ],
    },
    values: {
      eyebrow: "Warum Kompass",
      title: "Wir übersetzen nicht nur Worte — wir übersetzen Situationen",
      points: [
        { k: "Mehrsprachig", v: "Arabisch, Deutsch, Englisch und Persisch — unter einem Dach." },
        { k: "Vertraulich", v: "Ihre Geschichte bleibt bei uns. Immer diskret, immer respektvoll." },
        { k: "Persönlich", v: "Keine Callcenter. Sie sprechen jedes Mal mit derselben Person." },
        { k: "Lokal", v: "Mitten in Heidelberg — einfach erreichbar mit Bahn und Tram." },
      ],
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Sprechen wir über Ihre Situation",
      lead:
        "Schreiben Sie uns in einer unserer vier Sprachen. Wir antworten in der Regel innerhalb eines Werktags.",
      form: {
        name: "Ihr Name",
        email: "E-Mail-Adresse",
        subject: "Betreff",
        message: "Wie können wir Ihnen helfen?",
        submit: "Nachricht senden",
        sending: "Wird gesendet…",
        success: "Vielen Dank! Wir haben Ihre Nachricht erhalten und melden uns in Kürze.",
        error: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder rufen Sie uns an.",
      },
      info: {
        address_label: "Büro",
        address: "Eppelheimer Str. 21, 69115 Heidelberg",
        phone_label: "Telefon",
        phone: "+49 178 164 78 73",
        email_label: "E-Mail",
        email: "info@kompass-heidelberg.de",
        web_label: "Website",
        web: "www.kompass-heidelberg.de",
        hours_label: "Öffnungszeiten",
        hours: "Nach Vereinbarung",
      },
    },
    footer: {
      tagline: "Service für Migranten",
      rights: "Alle Rechte vorbehalten.",
      built: "Heidelberg, Deutschland",
    },
  },

  ar: {
    nav: {
      about: "من نحن",
      services: "خدماتنا",
      values: "لماذا كومباس",
      contact: "اتصل بنا",
      cta: "احجز استشارة",
    },
    hero: {
      eyebrow: "هايدلبرغ · خدمات للمهاجرين",
      title_1: "بوصلتك في",
      title_accent: "حياتك الجديدة في ألمانيا",
      subtitle:
        "كومباس مكتب للترجمة والاستشارة في شؤون الهجرة واللجوء في هايدلبرغ. نرافقك في الإجراءات الرسمية، والتسجيلات، والمواعيد مع الدوائر الحكومية، والبحث عن الأوسبيلدونغ — بهدوء واحترافية وبلغتك.",
      cta_primary: "احجز استشارة مجانية",
      cta_secondary: "تعرّف على خدماتنا",
      stat_1: "لغات",
      stat_2: "سنوات خبرة",
      stat_3: "عائلة نرافقها",
    },
    about: {
      eyebrow: "عن كومباس",
      title: "شريكك الموثوق في حياتك الجديدة في ألمانيا",
      lead:
        "نعرف كم تبدو البيروقراطية الألمانية مرهقة — رسائل بلغة رسمية، استمارات معقدة، وانتظار طويل في دائرة الأجانب. كومباس يقف إلى جانبك، يُترجم، يشرح، ويسير معك خطوة بخطوة.",
      pillars: [
        {
          title: "خدمة احترافية",
          text: "استشارات متخصصة لاندماج ناجح في ألمانيا — واضحة، دقيقة، وموثوقة.",
        },
        {
          title: "نصيحة موثوقة",
          text: "مرافقة من الدرجة الأولى وحلول مفصّلة تناسب وضعك الفردي.",
        },
        {
          title: "فريق ذو خبرة",
          text: "سنوات من العمل مع المهاجرين واللاجئين — نفهم العالمين معاً.",
        },
      ],
    },
    services: {
      eyebrow: "خدماتنا",
      title: "كل ما تحتاجه، في مكان واحد",
      subtitle:
        "من أول موعد لك في دائرة الأجانب وحتى توقيع عقد التدريب المهني — نحن معك على طول الطريق.",
      list: [
        {
          title: "دعم المستثمرين",
          text: "مرافقة شاملة للمستثمرين والراغبين بتأسيس شركة في ألمانيا — من الفكرة إلى الاستشارة القانونية والتنفيذ.",
        },
        {
          title: "استشارات الهجرة واللجوء",
          text: "استشارات شخصية في قضايا الهجرة والإقامة واللجوء. نُترجم القرارات ونُجهّز الأوراق ونشرح لك خياراتك.",
        },
        {
          title: "مرافقة للمواعيد الرسمية",
          text: "نرافقك إلى الدوائر الحكومية والأطباء والمدارس ودائرة الأجانب — ونُترجم لك مباشرةً حتى تفهم كل كلمة.",
        },
        {
          title: "تسجيلات ومعاملات",
          text: "تسجيل العنوان، التأمين الصحي، إعانة الأطفال، إعانة الوالدين، الرقم الضريبي — نملأ الاستمارات معك ونُقدّمها بشكل صحيح.",
        },
        {
          title: "البحث عن الأوسبيلدونغ",
          text: "ندعم الشباب المهاجرين في إيجاد فرصة تدريب مهني ونرافقهم في كامل عملية التقديم.",
        },
        {
          title: "هجرة الكفاءات",
          text: "الاعتراف بالشهادات الأجنبية، البحث عن عمل، تجهيز ملف الفيزا — طريقك إلى العمل في ألمانيا.",
        },
        {
          title: "خدمات الترجمة",
          text: "ترجمة موثوقة من العربية ↔ الألمانية ↔ الإنجليزية للشهادات والرسائل والعقود والأوراق الرسمية.",
        },
        {
          title: "دعم الاندماج",
          text: "مساعدتك في إيجاد سكن، التعامل مع الدوائر الرسمية، وفهم تفاصيل الحياة اليومية.",
        },
      ],
    },
    values: {
      eyebrow: "لماذا كومباس",
      title: "نحن لا نُترجم الكلمات فقط — نُترجم المواقف",
      points: [
        { k: "متعدد اللغات", v: "العربية، الألمانية، الإنجليزية والفارسية تحت سقف واحد." },
        { k: "سرّية تامة", v: "قصتك تبقى عندنا. باحترام كامل وخصوصية تامة." },
        { k: "تواصل شخصي", v: "لا مراكز اتصال. تتحدث في كل مرة مع نفس الشخص." },
        { k: "في قلب هايدلبرغ", v: "موقع مركزي يسهل الوصول إليه بالقطار والترام." },
      ],
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "لنتحدث عن وضعك",
      lead: "اكتب لنا بأي من لغاتنا الأربع، وسنرد عادةً خلال يوم عمل واحد.",
      form: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        subject: "الموضوع",
        message: "كيف يمكننا مساعدتك؟",
        submit: "إرسال الرسالة",
        sending: "جارٍ الإرسال…",
        success: "شكراً لك! استلمنا رسالتك وسنتواصل معك قريباً.",
        error: "حدث خطأ ما. يُرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.",
      },
      info: {
        address_label: "العنوان",
        address: "Eppelheimer Str. 21, 69115 Heidelberg",
        phone_label: "الهاتف",
        phone: "+49 178 164 78 73",
        email_label: "البريد الإلكتروني",
        email: "info@kompass-heidelberg.de",
        web_label: "الموقع",
        web: "www.kompass-heidelberg.de",
        hours_label: "ساعات العمل",
        hours: "بموعد مسبق",
      },
    },
    footer: {
      tagline: "خدمات للمهاجرين",
      rights: "جميع الحقوق محفوظة.",
      built: "هايدلبرغ، ألمانيا",
    },
  },

  fa: {
    nav: {
      about: "درباره ما",
      services: "خدمات",
      values: "چرا کمپاس",
      contact: "تماس",
      cta: "رزرو مشاوره",
    },
    hero: {
      eyebrow: "هایدلبرگ · خدمات برای مهاجران",
      title_1: "قطب‌نمای شما در",
      title_accent: "زندگی در آلمان",
      subtitle:
        "کمپاس یک دفتر ترجمه و مشاوره برای مهاجران در هایدلبرگ است. ما شما را در امور مهاجرت، پناهندگی، اداری، ثبت‌نام‌ها و یافتن اوسبیلدونگ همراهی می‌کنیم — با آرامش، حرفه‌ای و به زبان شما.",
      cta_primary: "رزرو مشاوره رایگان",
      cta_secondary: "خدمات ما",
      stat_1: "زبان",
      stat_2: "سال تجربه",
      stat_3: "خانواده همراهی‌شده",
    },
    about: {
      eyebrow: "درباره کمپاس",
      title: "شریکی قابل اعتماد برای زندگی جدید شما در آلمان",
      lead:
        "می‌دانیم که بوروکراسی آلمان چقدر می‌تواند خسته‌کننده باشد — نامه‌های رسمی، فرم‌های پیچیده و انتظارهای طولانی در اداره اتباع خارجی. کمپاس کنار شماست؛ ترجمه می‌کند، توضیح می‌دهد و قدم به قدم همراه شما می‌آید.",
      pillars: [
        {
          title: "خدمت حرفه‌ای",
          text: "مشاوره تخصصی برای ادغام موفق در آلمان — شفاف، دقیق و قابل اعتماد.",
        },
        {
          title: "مشاوره مطمئن",
          text: "همراهی درجه‌یک و راه‌حل‌های متناسب با شرایط شخصی شما.",
        },
        {
          title: "تیم باتجربه",
          text: "سال‌ها تجربه در کار با مهاجران و پناهندگان — هر دو دنیا را می‌شناسیم.",
        },
      ],
    },
    services: {
      eyebrow: "خدمات ما",
      title: "هرآنچه نیاز دارید، در یک مکان",
      subtitle:
        "از اولین قرار شما در اداره اتباع تا امضای قرارداد اوسبیلدونگ — ما در تمام مسیر همراه شما هستیم.",
      list: [
        {
          title: "پشتیبانی سرمایه‌گذاران",
          text: "همراهی کامل برای سرمایه‌گذاران و کارآفرینانی که می‌خواهند در آلمان شرکت تأسیس کنند — از ایده تا مشاوره حقوقی و اجرا.",
        },
        {
          title: "مشاوره مهاجرت و پناهندگی",
          text: "مشاوره شخصی در مسائل مهاجرت، اقامت و پناهندگی. تصمیمات را ترجمه می‌کنیم، مدارک را آماده و گزینه‌ها را شرح می‌دهیم.",
        },
        {
          title: "همراهی به قرارهای رسمی",
          text: "ما شما را به ادارات، پزشکان، مدارس و اداره اتباع همراهی می‌کنیم و به‌صورت زنده ترجمه می‌کنیم.",
        },
        {
          title: "ثبت‌نام و کارهای اداری",
          text: "ثبت آدرس، بیمه درمانی، کیندرگلد، اِلترن‌گلد، شماره مالیاتی — همراه شما فرم‌ها را پر و درست ارسال می‌کنیم.",
        },
        {
          title: "یافتن اوسبیلدونگ",
          text: "از مهاجران جوان در یافتن دوره اوسبیلدونگ پشتیبانی می‌کنیم و کل روند درخواست را همراه‌شان طی می‌کنیم.",
        },
        {
          title: "مهاجرت متخصصان",
          text: "تأیید مدارک خارجی، جستجوی شغل، آماده‌سازی پرونده ویزا — راه شما برای کار در آلمان.",
        },
        {
          title: "خدمات ترجمه",
          text: "ترجمه قابل اعتماد عربی ↔ آلمانی ↔ انگلیسی برای مدارک، نامه‌ها، قراردادها و اسناد رسمی.",
        },
        {
          title: "پشتیبانی ادغام",
          text: "کمک در یافتن مسکن، تعامل با ادارات و درک زندگی روزمره در آلمان.",
        },
      ],
    },
    values: {
      eyebrow: "چرا کمپاس",
      title: "ما فقط کلمات را ترجمه نمی‌کنیم — موقعیت‌ها را ترجمه می‌کنیم",
      points: [
        { k: "چندزبانه", v: "عربی، آلمانی، انگلیسی و فارسی زیر یک سقف." },
        { k: "محرمانه", v: "داستان شما نزد ما می‌ماند. همیشه با احترام و حفظ حریم خصوصی." },
        { k: "ارتباط شخصی", v: "هیچ مرکز تماسی نیست. هر بار با همان شخص صحبت می‌کنید." },
        { k: "در قلب هایدلبرگ", v: "موقعیتی مرکزی، با دسترسی آسان به ترام و قطار." },
      ],
    },
    contact: {
      eyebrow: "تماس",
      title: "بیایید درباره وضعیت شما صحبت کنیم",
      lead: "به هر یک از چهار زبان ما بنویسید. معمولاً ظرف یک روز کاری پاسخ می‌دهیم.",
      form: {
        name: "نام شما",
        email: "ایمیل",
        subject: "موضوع",
        message: "چگونه می‌توانیم کمک کنیم؟",
        submit: "ارسال پیام",
        sending: "در حال ارسال…",
        success: "متشکریم! پیام شما دریافت شد و به‌زودی پاسخ می‌دهیم.",
        error: "مشکلی پیش آمد. لطفاً دوباره تلاش کنید یا تماس بگیرید.",
      },
      info: {
        address_label: "دفتر",
        address: "Eppelheimer Str. 21, 69115 Heidelberg",
        phone_label: "تلفن",
        phone: "+49 178 164 78 73",
        email_label: "ایمیل",
        email: "info@kompass-heidelberg.de",
        web_label: "وب‌سایت",
        web: "www.kompass-heidelberg.de",
        hours_label: "ساعات کاری",
        hours: "با تعیین وقت قبلی",
      },
    },
    footer: {
      tagline: "خدمات برای مهاجران",
      rights: "تمامی حقوق محفوظ است.",
      built: "هایدلبرگ، آلمان",
    },
  },
};

export const SERVICE_ICONS = [
  "Briefcase",
  "Scale",
  "UsersRound",
  "FileText",
  "GraduationCap",
  "Plane",
  "Languages",
  "Home",
];
